import { Button, CircularProgress, Grid, IconButton, Tooltip } from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloudDownload from '@material-ui/icons/CloudDownload';
import DeleteIcon from '@material-ui/icons/Delete';
import ErrorIcon from '@material-ui/icons/Error';
import { excluirAlert } from 'app/services/alerts';
import { anexoApi } from 'app/services/api/anexoApi';
import * as Actions from 'app/store/actions';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { IoIosCloseCircle, IoMdCloudDownload } from 'react-icons/io';
import { useDispatch } from 'react-redux';
import { anexoStyles } from './styles';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const textStyle = {
  fontSize: 20,
  lineHeight: 1.25,
  color: '#000000',
  textAlign: 'center',
};

// https://react-dropzone.js.org/
const TAMANHO_MAXIMO_5_MB = 5242880;
const MSG_ERRO_MAX_SIZE_FILES = 'É permitido anexar apenas 5 arquivos de 5MB cada.';
const MSG_ERRO_FORMATO =
  'É permitido anexar somente arquivos nos formatos: PDF, DOC, DOCX, XLS, XLSX, PNG, JPEG e CSV.';

function AnexoDropzone({
  id,
  onDropFile,
  maxFiles = 5,
  maxSize = TAMANHO_MAXIMO_5_MB,
  accept = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.csv'],
  files = [],
  text = (
    <>
      <Grid container justify="center" alignItems="center" spacing={2}>
        <Grid item>
          <AttachFileIcon style={{ color: '#000', fontSize: 32 }} />
        </Grid>
        <Grid item>
          <p style={textStyle}>Arraste os arquivos ou clique para adicioná-los.</p>
          <p style={{ ...textStyle, fontSize: 15 }}>Formatos permitidos: PDF, DOC, DOCX, XLS, XLSX, PNG, JPEG, CSV</p>
        </Grid>
      </Grid>
    </>
  ),
  linkDownload,
  onRemove = () => {},
  msgErroMaxSizeFiles = MSG_ERRO_MAX_SIZE_FILES,
  msgErroFormato = MSG_ERRO_FORMATO,
  showThumbnail,
}) {
  const dispatch = useDispatch();
  const styles = anexoStyles();

  const setFiles = (arquivos) => {
    onDropFile({ target: { id, value: arquivos } });
  };

  const showMessage = (message, variant) => {
    dispatch(
      Actions.showMessage({
        message,
        variant,
      })
    );
  };

  const handleRejectedFiles = (rejectedFiles) => {
    rejectedFiles.forEach((file) => {
      if (file.errors[0].code === 'file-invalid-type') {
        showMessage(msgErroFormato, 'error');
      } else if (file.errors[0].code === 'file-too-large') {
        showMessage(msgErroMaxSizeFiles, 'error');
      }
    });
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    maxSize,
    accept,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        handleRejectedFiles(rejectedFiles);
      }
      const arquivos = acceptedFiles.map((file) =>
        Object.assign(file, {
          id: undefined,
          nome: file.name,
          contentType: file.type,
          preview: URL.createObjectURL(file),
        })
      );

      const anexos = files.concat(arquivos);
      if (anexos.length > maxFiles) {
        showMessage(msgErroMaxSizeFiles, 'error');
      } else {
        setFiles(anexos);
      }
    },
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const removeFile = (file) => {
    const arquivos = [...files];
    arquivos.splice(arquivos.indexOf(file), 1);
    setFiles(arquivos);
    onRemove(file);
  };

  const onDownload = async (file) => {
    const response = await anexoApi().downloadAnexo(`${linkDownload}/${file.id}`);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', file.nome);
    document.body.appendChild(link);
    link.click();
  };

  const renderItemAnexo = (file, linkDownload) => {
    return (
      <Grid item key={file.nome} xs={12} style={{ paddingBottom: '5px' }}>
        <Button
          fullWidth
          variant="outlined"
          color="default"
          style={{
            justifyContent: 'left',
            pointerEvents: 'none',
            background: 'rgba(100, 96, 94, 0.53)',
            color: '#fff',
            fontSize: 15,
          }}
        >
          <IoIosCloseCircle
            size={20}
            style={{ pointerEvents: 'auto', marginRight: '5px', color: '#32302f', opacity: 1 }}
            onClick={() => {
              excluirAlert({
                confirmar: () => {
                  removeFile(file);
                },
              });
            }}
          />
          {!!linkDownload && (
            <IoMdCloudDownload
              size={20}
              style={{ pointerEvents: 'auto', marginRight: '5px' }}
              onClick={() => onDownload(file)}
            />
          )}
          {file.nome} - {file.size && file.size / 1024 / 1024} bytes
        </Button>
      </Grid>
    );
  };

  const TooltipError = ({ error, children }) => {
    if (error) {
      return (
        <Tooltip title={error} placement="bottom">
          {children}
        </Tooltip>
      );
    }
    return <>{children}</>;
  };

  const renderThumbnail = (file, index) => {
    return (
      <div key={file.id} className={styles.imagemContainer}>
        <TooltipError error={file.error}>
          <a href={file.url} data-lightbox={`imagens-produto-${file.id}`}>
            <div
              role="button"
              tabIndex={0}
              className={clsx(
                styles.productImageItem,
                'flex items-center justify-center relative w-128 h-128 rounded-4 mx-8 mb-4 overflow-hidden cursor-pointer shadow-1 hover:shadow-5',
                file.principal && 'featured',
                file.error && 'error'
              )}
              key={file.id}
            >
              <img
                className={clsx('max-w-none', 'w-auto', 'h-full', file.loading ? styles.imageLoading : '')}
                src={file.thumbUrl}
                alt={`Imagem número ${index + 1}`}
              />
              {file.loading && <CircularProgress className={styles.imageIcon} />}
              {file.error && <ErrorIcon fontSize="large" className={clsx(styles.imageIcon, 'error')} />}
            </div>
          </a>
        </TooltipError>

        <div className={styles.buttonContainer}>
          <Tooltip title="Excluir imagem" placement="bottom" className={styles.tooltip}>
            <IconButton
              className={styles.buttonDelete}
              aria-label="delete"
              onClick={() =>
                excluirAlert({
                  confirmar: () => {
                    removeFile(file);
                  },
                })
              }
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download imagem" placement="bottom" className={styles.tooltip}>
            <IconButton className={styles.buttonDownload} aria-label="download" onClick={() => onDownload(file)}>
              <CloudDownload />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <div {...getRootProps({ style: { ...style, backgroundColor: '#F0EFEF', opacity: 0.53 } })}>
          <input {...getInputProps()} />
          {text}
        </div>
      </Grid>
      {files && files.length > 0 && (
        <>
          <Grid item xs={12}>
            <h4>Arquivos adicionados:</h4>
          </Grid>
          {!!showThumbnail && (
            <>
              {files
                .filter((file) => file.contentType === 'image/jpeg' || file.contentType === 'image/png')
                .map((file, index) => renderThumbnail(file, index))}
              {files
                .filter((file) => file.contentType !== 'image/jpeg' && file.contentType !== 'image/png')
                .map((file, index) => renderItemAnexo(file, linkDownload))}
            </>
          )}

          {!showThumbnail && files.map((file, index) => renderItemAnexo(file, linkDownload))}
        </>
      )}
    </Grid>
  );
}

export default AnexoDropzone;
